export const DEFAULT_DOCUMENT_TYPE = 'invoice'

import {
  getMultiPoAllocationSummary,
  listOpenPoAllocations,
  hasReconciledPoAllocation,
} from './invoiceMultiDocRules'
import { getInvoiceOcrPurchaseOrderCandidates } from './invoiceMatchPoSuggestionsHelpers'
import { filterInvoicesInReconciliationAlertWindow } from './reconciliationAlertCutoff'

export const MANDATORY_UPLOAD_FIELDS = [
  'supplier',
  'purchaseOrderNumber',
  'invoiceNumber',
  'invoiceTotal',
  'invoiceDate',
]

export const MANDATORY_UPLOAD_FIELD_LABELS = {
  supplier: 'Supplier',
  purchaseOrderNumber: 'PO number',
  invoiceNumber: 'Invoice number',
  invoiceTotal: 'Invoice total (including VAT)',
  invoiceDate: 'Invoice date',
}

export const INVOICE_TOTAL_HELPER_TEXT =
  'Enter the full amount on the invoice, including VAT.'

export const OCR_FIELD_OUTCOME = {
  ACCEPTED: 'accepted',
  CORRECTED: 'corrected',
  CLEARED: 'cleared',
}

export const AI_SUGGESTED_BADGE_LABEL = 'Suggested'
export const AI_EDITED_BADGE_LABEL = 'Edited'
export const AI_ESTIMATED_BADGE_LABEL = 'Estimated'
export const NON_OMS_SUPPLIER_BADGE_LABEL = 'Non-OMS supplier'

/** True when the upload has a supplier name but no linked VenueSupplier id. */
export function isNonOmsSupplierUpload(file = {}) {
  const hasTitle = Boolean(String(file.supplierTitle || '').trim())
  if (!hasTitle) {
    return false
  }
  return !isLikelyVenueSupplierId(file.supplier)
}

export function isLikelyVenueSupplierId(value) {
  return /^[a-fA-F0-9]{24}$/.test(String(value || ''))
}

export function getVenueSuppliersPath(venueId) {
  if (!venueId) return '/suppliers'
  return `/v/${venueId}/suppliers`
}

/** Printed Non-OMS name that can be created as a venue supplier mid-upload. */
export function getCreateSupplierFromUploadTitle(file = {}) {
  return String(file?.supplierTitle || '').trim()
}

export function canCreateSupplierFromUpload(file = {}) {
  return (
    isNonOmsSupplierUpload(file) &&
    Boolean(getCreateSupplierFromUploadTitle(file))
  )
}

export const CREATE_SUPPLIER_FROM_UPLOAD_COPY = {
  noteSuffix:
    'Or create them here as a venue supplier (same as a manual order) so you can file and download without leaving upload. Auto-match still needs a linked supplier or an explicit PO.',
  buttonLabel: title => `Create “${title}” as supplier`,
  creatingLabel: 'Creating supplier…',
  successCreated: 'Supplier created — selected for this upload.',
  successReused:
    'Supplier already on Your Suppliers — selected for this upload.',
}

/** Provenance of an invoice total (mirrors express INVOICE_TOTAL_SOURCE). */
export const INVOICE_TOTAL_SOURCE = {
  OCR: 'ocr',
  LINE_SUM: 'line_sum',
  MANUAL: 'manual',
}

export function buildEmptyUploadFileFields(filename, base64) {
  return {
    filename,
    image: { base64 },
    supplier: null,
    supplierTitle: '',
    purchaseOrderNumber: '',
    linkedOrderId: null,
    documentType: DEFAULT_DOCUMENT_TYPE,
    comment: '',
    invoiceNumber: '',
    invoiceDate: null,
    invoiceTotalInput: '',
    invoiceTotalSource: null,
    invoiceId: null,
    ocrStatus: 'idle',
    ocrSuggested: null,
    ocrWarnings: [],
    ocrError: null,
  }
}

/**
 * Seed PO / supplier from a GR → Invoices upload deep link when OCR left
 * those fields empty (never overwrite a real OCR suggestion).
 */
export function applyUploadPrefillToFile(file = {}, prefill = {}) {
  if (
    !prefill ||
    (!prefill.po && !prefill.supplierId && !prefill.supplierTitle)
  ) {
    return file
  }

  const next = { ...file }
  const po = String(prefill.po || '').trim()
  if (po && !String(next.purchaseOrderNumber || '').trim()) {
    next.purchaseOrderNumber = po
  }

  const supplierId = String(prefill.supplierId || '').trim()
  const supplierTitle = String(prefill.supplierTitle || '').trim()
  if (supplierId && !next.supplier) {
    next.supplier = supplierId
    if (supplierTitle && !String(next.supplierTitle || '').trim()) {
      next.supplierTitle = supplierTitle
    }
  } else if (supplierTitle && !String(next.supplierTitle || '').trim()) {
    next.supplierTitle = supplierTitle
  }

  return next
}

/**
 * Parse a user-entered currency amount into integer pennies.
 * Accepts £, commas, and parentheses for credits e.g. "(10.00)".
 * Returns null when empty/invalid.
 */
export function parseInvoiceTotalToPennies(value) {
  if (value == null) {
    return null
  }

  let raw = String(value).trim()
  if (!raw) {
    return null
  }

  raw = raw.replace(/£/g, '').replace(/,/g, '').replace(/\s+/g, '')
  const negative = /^\(.*\)$/.test(raw) || raw.startsWith('-')
  raw = raw.replace(/[()]/g, '').replace(/^-/, '')

  if (!/^\d+(\.\d{1,2})?$/.test(raw)) {
    return null
  }

  const [poundsPart, fractionPart = ''] = raw.split('.')
  const pennies =
    Number(poundsPart) * 100 + Number((fractionPart + '00').slice(0, 2))

  if (!Number.isFinite(pennies)) {
    return null
  }

  return negative ? -pennies : pennies
}

export function isValidInvoiceTotalInput(value) {
  return parseInvoiceTotalToPennies(value) != null
}

/** Normalise Date / moment / ISO string to YYYY-MM-DD, or null. */
export function normaliseInvoiceDateInput(value) {
  if (value == null || value === '') {
    return null
  }

  if (typeof value?.format === 'function') {
    return value.format('YYYY-MM-DD')
  }

  if (value instanceof Date && !Number.isNaN(value.getTime())) {
    return value.toISOString().slice(0, 10)
  }

  const raw = String(value).trim()
  if (/^\d{4}-\d{2}-\d{2}$/.test(raw)) {
    return raw
  }

  const parsed = new Date(raw)
  if (!Number.isNaN(parsed.getTime())) {
    return parsed.toISOString().slice(0, 10)
  }

  return null
}

/** Soft upload warnings — informational only; never block confirm. */
export const UPLOAD_SOFT_WARNING = {
  DELIVERY_NOTE: 'delivery_note',
  MISSING_TOTAL: 'missing_total',
  ODD_TOTAL: 'odd_total',
  NON_OMS_SUPPLIER: 'non_oms_supplier',
}

/** Totals above this (pennies) get an “odd total” soft warning. £50,000. */
export const ODD_INVOICE_TOTAL_WARNING_PENNIES = 5_000_000

export function isLikelyDeliveryNoteUpload(file = {}) {
  const ocrWarnings = Array.isArray(file.ocrWarnings) ? file.ocrWarnings : []
  const documentTypeGuess =
    file.ocrSuggested?.documentTypeGuess || file.detectedDocumentType || null

  return (
    ocrWarnings.includes('looks_like_delivery_note') ||
    documentTypeGuess === 'delivery_note'
  )
}

/**
 * Soft warnings for the confirm modal. Never used to disable Confirm.
 * Covers delivery notes, missing totals, and unusually large totals.
 */
export function getUploadSoftWarnings(file = {}) {
  const warnings = []
  const ocrWarnings = Array.isArray(file.ocrWarnings) ? file.ocrWarnings : []
  const suggested = file.ocrSuggested || {}
  const totalPennies = parseInvoiceTotalToPennies(file.invoiceTotalInput)
  const looksLikeDeliveryNote = isLikelyDeliveryNoteUpload(file)

  if (looksLikeDeliveryNote) {
    warnings.push({
      code: UPLOAD_SOFT_WARNING.DELIVERY_NOTE,
      message:
        'This looks like a delivery note, not an invoice. You can still confirm — matching usually needs a real invoice with a total.',
    })
  }

  const ocrMissingTotal =
    ocrWarnings.includes('gross_total_not_found') ||
    (file.ocrStatus === 'suggested' &&
      suggested.grossTotal == null &&
      suggested.documentTypeGuess !== 'delivery_note')

  if (totalPennies == null && (looksLikeDeliveryNote || ocrMissingTotal)) {
    warnings.push({
      code: UPLOAD_SOFT_WARNING.MISSING_TOTAL,
      message: looksLikeDeliveryNote
        ? 'No total was found (common on delivery notes). Leave it blank, or enter the total if you have a matching invoice.'
        : 'No invoice total was found on the document. Please enter the total including VAT if you can see one.',
    })
  }

  if (
    totalPennies != null &&
    Math.abs(totalPennies) >= ODD_INVOICE_TOTAL_WARNING_PENNIES
  ) {
    warnings.push({
      code: UPLOAD_SOFT_WARNING.ODD_TOTAL,
      message:
        'This total looks unusually large. Double-check it before confirming.',
    })
  }

  if (isNonOmsSupplierUpload(file)) {
    const printedName = String(file.supplierTitle || '').trim()
    warnings.push({
      code: UPLOAD_SOFT_WARNING.NON_OMS_SUPPLIER,
      message: printedName
        ? `“${printedName}” isn’t on Your Suppliers for this venue. Kept as Non-OMS — pick or add the right supplier; we won’t silently link another.`
        : 'No OMS supplier is linked. Pick or add the right supplier under Your Suppliers — we won’t guess one.',
    })
  }

  return warnings
}

/**
 * The gross-total estimate the AI summed from the document's line items.
 * Only available when OCR found no printed total but could add up the lines.
 * Returns { pennies, formatted, count } or null.
 */
export function getInvoiceTotalEstimateFromLines(file = {}) {
  const suggested = file.ocrSuggested || {}
  const pennies = suggested.estimatedGrossTotalFromLines
  if (pennies == null || !Number.isFinite(Number(pennies))) {
    return null
  }
  return {
    pennies: Number(pennies),
    formatted: formatPenniesAsPoundsInput(pennies),
    count: Number(suggested.lineItemsCount) || 0,
  }
}

/**
 * Whether we can offer a one-click "use estimated total" action:
 * an estimate exists and the user has not already entered a total.
 */
export function canUseInvoiceTotalEstimate(file = {}) {
  if (parseInvoiceTotalToPennies(file.invoiceTotalInput) != null) {
    return false
  }
  return getInvoiceTotalEstimateFromLines(file) != null
}

/**
 * Apply the line-item estimate to the total input and stamp provenance.
 * Returns a new file object (does not mutate).
 */
export function applyInvoiceTotalEstimate(file = {}) {
  const estimate = getInvoiceTotalEstimateFromLines(file)
  if (!estimate) {
    return file
  }
  return {
    ...file,
    invoiceTotalInput: estimate.formatted,
    invoiceTotalSource: INVOICE_TOTAL_SOURCE.LINE_SUM,
  }
}

/**
 * Whether the current total input is the accepted line-item estimate
 * (used for the "AI estimated" badge and provenance).
 */
export function isInvoiceTotalFromLineEstimate(file = {}) {
  if (file.invoiceTotalSource !== INVOICE_TOTAL_SOURCE.LINE_SUM) {
    return false
  }
  const estimate = getInvoiceTotalEstimateFromLines(file)
  if (!estimate) {
    return false
  }
  return parseInvoiceTotalToPennies(file.invoiceTotalInput) === estimate.pennies
}

export function getMissingMandatoryUploadFields(file = {}) {
  const missing = []

  const hasSupplier =
    (file.supplier != null && file.supplier !== '') ||
    Boolean(String(file.supplierTitle || '').trim())

  if (!hasSupplier) {
    missing.push('supplier')
  }

  // PO is optional on confirm:
  // - Credit notes: pick OMS PO later via Apply
  // - Invoices ordered outside OMS: confirm first, then Create order from invoice
  //   (which creates the PO and auto-links). Blank PO is OK.

  if (!String(file.invoiceNumber || '').trim()) {
    missing.push('invoiceNumber')
  }

  // Delivery notes often have no total — warn instead of blocking (Step 19).
  if (
    !isLikelyDeliveryNoteUpload(file) &&
    parseInvoiceTotalToPennies(file.invoiceTotalInput) == null
  ) {
    missing.push('invoiceTotal')
  }

  if (!normaliseInvoiceDateInput(file.invoiceDate)) {
    missing.push('invoiceDate')
  }

  return missing
}

export function isUploadFileComplete(file = {}) {
  return getMissingMandatoryUploadFields(file).length === 0
}

export function isUploadDisabled(selectedFiles = []) {
  if (!selectedFiles.length) {
    return true
  }

  return selectedFiles.some(file => !isUploadFileComplete(file))
}

export function getMissingMandatoryUploadLabels(file = {}) {
  return getMissingMandatoryUploadFields(file).map(
    field => MANDATORY_UPLOAD_FIELD_LABELS[field] || field,
  )
}

/**
 * Build the structured fields sent via enrich after confirm.
 * Keeps AddInvoices free of pennies/date formatting details.
 */
export function buildStructuredUploadFields(file = {}) {
  return {
    supplier: file.supplier || null,
    supplierTitle: String(file.supplierTitle || '').trim(),
    purchaseOrderNumber: String(file.purchaseOrderNumber || '').trim(),
    linkedOrderId: file.linkedOrderId || null,
    documentType: file.documentType || DEFAULT_DOCUMENT_TYPE,
    comment: String(file.comment || '').trim(),
    invoiceNumber: String(file.invoiceNumber || '').trim(),
    invoiceDate: normaliseInvoiceDateInput(file.invoiceDate),
    invoiceTotal: parseInvoiceTotalToPennies(file.invoiceTotalInput),
  }
}

export function formatPenniesAsPoundsInput(pennies) {
  if (pennies == null || pennies === '') {
    return ''
  }
  const value = Number(pennies)
  if (!Number.isFinite(value)) {
    return ''
  }
  const negative = value < 0
  const abs = Math.abs(Math.round(value))
  const pounds = `${Math.floor(abs / 100)}.${String(abs % 100).padStart(
    2,
    '0',
  )}`
  return negative ? `-${pounds}` : pounds
}

export function mapOcrDocumentTypeToUpload(documentTypeGuess) {
  const guess = String(documentTypeGuess || '')
    .trim()
    .toLowerCase()
  if (guess === 'credit_note' || guess === 'creditnote') {
    return 'creditNote'
  }
  // delivery_note / statement / unknown → stay Invoice (amber Check type in UI)
  return DEFAULT_DOCUMENT_TYPE
}

/** Legal / trading suffixes that usually differ between invoice print and OMS titles. */
const SUPPLIER_LEGAL_SUFFIX_PATTERN =
  /\b(limited|ltd|plc|llp|inc|corp|corporation|company|co|bros|brothers|group|holdings|uk|the)\b/g

function stemSupplierToken(token) {
  const value = String(token || '')
  if (value.length > 4 && value.endsWith('s') && !value.endsWith('ss')) {
    return value.slice(0, -1)
  }
  return value
}

function normaliseSupplierMatchText(value) {
  return String(value || '')
    .toLowerCase()
    .replace(SUPPLIER_LEGAL_SUFFIX_PATTERN, ' ')
    .replace(/[^a-z0-9]+/g, ' ')
    .trim()
    .replace(/\s+/g, ' ')
}

function supplierMatchTokens(normalised) {
  return normalised
    .split(' ')
    .map(stemSupplierToken)
    .filter(token => token.length >= 2)
}

function scoreSupplierNameMatch(ocrName, supplierTitle) {
  const needle = normaliseSupplierMatchText(ocrName)
  const title = normaliseSupplierMatchText(supplierTitle)
  if (!needle || !title) {
    return 0
  }
  if (needle === title) {
    return 5
  }
  if (title.startsWith(needle) || needle.startsWith(title)) {
    return 4
  }
  if (title.includes(needle) || needle.includes(title)) {
    return 3
  }

  const needleTokens = supplierMatchTokens(needle)
  const titleTokens = new Set(supplierMatchTokens(title))
  if (!needleTokens.length || !titleTokens.size) {
    return 0
  }
  const shared = needleTokens.filter(token => titleTokens.has(token))
  if (!shared.length) {
    return 0
  }
  const coverage = shared.length / Math.max(needleTokens.length, 1)
  if (coverage >= 1) {
    return 3
  }
  if (coverage >= 0.5 && shared.some(token => token.length >= 4)) {
    return 2
  }
  return 0
}

/**
 * Rank active venue suppliers against an OCR / typed supplier name.
 * Deterministic (no AI call): exact / prefix / contains / token overlap.
 * @returns {{ venueSupplier: object, score: number }[]}
 */
export function suggestVenueSuppliersFromOcrName(
  supplierName,
  venueSuppliers = [],
  { limit = 3 } = {},
) {
  if (!String(supplierName || '').trim()) {
    return []
  }
  if (!Array.isArray(venueSuppliers) || !venueSuppliers.length) {
    return []
  }

  return venueSuppliers
    .map(venueSupplier => {
      const score = scoreSupplierNameMatch(
        supplierName,
        venueSupplier?.supplierTitle,
      )
      if (score <= 0) return null
      return { venueSupplier, score }
    })
    .filter(Boolean)
    .sort((a, b) => {
      if (b.score !== a.score) return b.score - a.score
      return String(a.venueSupplier?.supplierTitle || '').localeCompare(
        String(b.venueSupplier?.supplierTitle || ''),
      )
    })
    .slice(0, Math.max(1, Number(limit) || 3))
}

/**
 * Best-effort single venue-supplier match for autofill.
 * Only auto-selects a clear unique winner (score >= 3: exact / prefix /
 * contains). Weak token overlap (score 2) stays for suggestions only —
 * never silently link a different OMS supplier (e.g. Fife Creamery).
 */
export function matchVenueSupplierFromOcrName(
  supplierName,
  venueSuppliers = [],
) {
  const suggestions = suggestVenueSuppliersFromOcrName(
    supplierName,
    venueSuppliers,
    { limit: 3 },
  )
  if (!suggestions.length) {
    return null
  }

  const top = suggestions[0]
  const second = suggestions[1]
  const uniqueTop = !second || second.score < top.score
  if (top.score >= 3 && uniqueTop) {
    return top.venueSupplier
  }
  return null
}

/**
 * Apply OCR API `data` onto an upload file row (fail-open safe).
 */
export function applyOcrSuggestionsToUploadFile(
  file = {},
  ocrData = {},
  venueSuppliers = [],
) {
  const suggested = ocrData?.suggested || null
  const outcome =
    ocrData?.outcome || (ocrData?.success ? 'suggested' : 'failed')
  const base = {
    ...file,
    invoiceId: ocrData?.invoiceId || file.invoiceId || null,
    ocrStatus: outcome === 'suggested' && suggested ? 'suggested' : outcome,
    ocrSuggested: suggested,
    ocrWarnings: Array.isArray(ocrData?.warnings) ? ocrData.warnings : [],
    ocrError: ocrData?.error || null,
  }

  if (!suggested) {
    return base
  }

  const matchedSupplier = matchVenueSupplierFromOcrName(
    suggested.supplierName,
    venueSuppliers,
  )
  const omsSupplierSuggestions = suggestVenueSuppliersFromOcrName(
    suggested.supplierName,
    venueSuppliers,
    { limit: 3 },
  ).map(item => ({
    id: String(item.venueSupplier?._id || ''),
    title: item.venueSupplier?.supplierTitle || '',
    score: item.score,
  }))

  const hasPo = Boolean(String(file.purchaseOrderNumber || '').trim())
  const hasInvoiceNumber = Boolean(String(file.invoiceNumber || '').trim())
  const hasInvoiceDate = Boolean(normaliseInvoiceDateInput(file.invoiceDate))
  const hasTotal = Boolean(String(file.invoiceTotalInput || '').trim())
  const hasSupplierTitle = Boolean(String(file.supplierTitle || '').trim())
  const printedSupplierTitle = hasSupplierTitle
    ? String(file.supplierTitle || '').trim()
    : String(suggested.supplierName || '').trim()
  const linkedOmsSupplierId = resolveOcrLinkedSupplierId(
    file,
    matchedSupplier,
    printedSupplierTitle,
  )

  return {
    ...base,
    // Fill empty fields only — never clobber values the user already typed
    // (matches server applyOcrSuggestionsToInvoiceFields).
    purchaseOrderNumber: hasPo
      ? file.purchaseOrderNumber
      : suggested.poNumber || '',
    invoiceNumber: hasInvoiceNumber
      ? file.invoiceNumber
      : suggested.invoiceNumber || '',
    invoiceDate: hasInvoiceDate
      ? file.invoiceDate
      : normaliseInvoiceDateInput(suggested.invoiceDate) || null,
    invoiceTotalInput: hasTotal
      ? file.invoiceTotalInput
      : suggested.grossTotal != null
      ? formatPenniesAsPoundsInput(suggested.grossTotal)
      : '',
    invoiceTotalSource: hasTotal
      ? file.invoiceTotalSource || null
      : suggested.grossTotal != null
      ? INVOICE_TOTAL_SOURCE.OCR
      : file.invoiceTotalSource || null,
    documentType:
      file.documentType && file.documentType !== DEFAULT_DOCUMENT_TYPE
        ? file.documentType
        : mapOcrDocumentTypeToUpload(
            suggested.documentTypeGuess || ocrData.documentTypeGuess,
          ),
    supplier: linkedOmsSupplierId,
    supplierTitle: hasSupplierTitle
      ? file.supplierTitle
      : matchedSupplier?.supplierTitle &&
        linkedOmsSupplierId === matchedSupplier._id
      ? matchedSupplier.supplierTitle
      : suggested.supplierName || '',
    omsSupplierSuggestions,
  }
}

/** Only link an OMS supplier when the printed name aligns with the match. */
function resolveOcrLinkedSupplierId(
  file = {},
  matchedSupplier = null,
  printedSupplierTitle = '',
) {
  if (isLikelyVenueSupplierId(file.supplier)) {
    return file.supplier
  }
  if (!matchedSupplier?._id) {
    return null
  }
  const matchedTitle = String(matchedSupplier.supplierTitle || '').trim()
  if (!printedSupplierTitle || !matchedTitle) {
    return matchedSupplier._id
  }
  return feedbackValuesEqual(printedSupplierTitle, matchedTitle)
    ? matchedSupplier._id
    : null
}

/** Mark a review form field as user-edited so late OCR cannot overwrite it. */
export function touchUploadFileField(file = {}, fieldName) {
  if (!fieldName) return file
  const touched = new Set(file.userTouchedFields || [])
  touched.add(fieldName)
  return {
    ...file,
    userTouchedFields: [...touched],
  }
}

/**
 * User typed a PO (e.g. "TBC") instead of picking an OMS order — lock supplier
 * fields at their current values so late OCR cannot auto-fill an AI guess.
 */
export function applyManualPurchaseOrderTouchToUploadFile(
  file = {},
  poUpdate = {},
) {
  let next = touchUploadFileField(file, 'purchaseOrderNumber')
  if (!poUpdate.venueSupplierId) {
    next = touchUploadFileField(next, 'supplier')
    next = touchUploadFileField(next, 'supplierTitle')
  }
  return next
}

/**
 * When OCR finishes after the user started editing a sibling file, keep their
 * corrections (PO "TBC", invoice date, etc.) instead of replacing the row.
 */
export function mergeOcrResultPreservingUploadFileEdits(
  current = {},
  ocrFile = {},
) {
  const touched = new Set(current.userTouchedFields || [])
  const merged = {
    ...current,
    invoiceId: ocrFile.invoiceId || current.invoiceId,
    ocrStatus: ocrFile.ocrStatus,
    ocrSuggested: ocrFile.ocrSuggested,
    ocrWarnings: ocrFile.ocrWarnings,
    ocrError: ocrFile.ocrError,
    omsSupplierSuggestions: ocrFile.omsSupplierSuggestions,
    userTouchedFields: current.userTouchedFields,
  }

  const assignUnlessTouched = (field, value) => {
    if (!touched.has(field)) {
      merged[field] = value
    }
  }

  assignUnlessTouched(
    'purchaseOrderNumber',
    ocrFile.purchaseOrderNumber ?? current.purchaseOrderNumber,
  )
  if (!touched.has('purchaseOrderNumber')) {
    merged.linkedOrderId = ocrFile.linkedOrderId ?? current.linkedOrderId
  }
  assignUnlessTouched(
    'invoiceNumber',
    ocrFile.invoiceNumber ?? current.invoiceNumber,
  )
  assignUnlessTouched('invoiceDate', ocrFile.invoiceDate ?? current.invoiceDate)
  assignUnlessTouched(
    'invoiceTotalInput',
    ocrFile.invoiceTotalInput ?? current.invoiceTotalInput,
  )
  assignUnlessTouched(
    'invoiceTotalSource',
    ocrFile.invoiceTotalSource ?? current.invoiceTotalSource,
  )
  // Once the user edits any field (e.g. PO "TBC"), keep supplier as-is so late
  // OCR cannot replace a Non-OMS printed name with an OMS link or AI guess.
  if (touched.size > 0) {
    merged.supplier = current.supplier ?? null
    merged.supplierTitle = current.supplierTitle ?? ''
  } else {
    assignUnlessTouched('supplier', ocrFile.supplier ?? current.supplier)
    assignUnlessTouched(
      'supplierTitle',
      ocrFile.supplierTitle ?? current.supplierTitle,
    )
  }
  assignUnlessTouched(
    'documentType',
    ocrFile.documentType ?? current.documentType,
  )

  return merged
}

function feedbackValuesEqual(left, right) {
  if (left == null && right == null) return true
  if (left == null || right == null) return false
  if (typeof left === 'number' || typeof right === 'number') {
    return Number(left) === Number(right)
  }
  return String(left).trim() === String(right).trim()
}

/**
 * Light feedback loop: compare OCR suggestions to the user's confirmed values.
 * Mirrors express `buildInvoiceOcrFieldFeedback` for client-side confirm.
 */
export function buildUploadOcrFieldFeedback(file = {}) {
  const suggested = file.ocrSuggested || {}
  const finals = buildStructuredUploadFields(file)

  const rows = [
    ['supplier', suggested.supplierName, finals.supplierTitle || null],
    [
      'purchaseOrderNumber',
      suggested.poNumber,
      finals.purchaseOrderNumber || null,
    ],
    ['invoiceNumber', suggested.invoiceNumber, finals.invoiceNumber || null],
    ['invoiceDate', suggested.invoiceDate, finals.invoiceDate || null],
    ['invoiceTotal', suggested.grossTotal, finals.invoiceTotal],
    [
      'detectedDocumentType',
      suggested.documentTypeGuess,
      finals.documentType === 'creditNote' ? 'credit_note' : 'invoice',
    ],
  ]

  return rows
    .filter(
      ([, suggestion, finalValue]) => suggestion != null || finalValue != null,
    )
    .map(([field, suggestion, finalValue]) => ({
      field,
      suggestedValue: suggestion ?? null,
      finalValue: finalValue ?? null,
      outcome:
        finalValue == null || finalValue === ''
          ? OCR_FIELD_OUTCOME.CLEARED
          : feedbackValuesEqual(suggestion, finalValue)
          ? OCR_FIELD_OUTCOME.ACCEPTED
          : OCR_FIELD_OUTCOME.CORRECTED,
    }))
}

/**
 * Which form fields still match the OCR suggestion (for subtle AI badges).
 */
export function getAiSuggestedFieldFlags(file = {}) {
  const suggested = file.ocrSuggested
  if (!suggested || file.ocrStatus !== 'suggested') {
    return {
      supplier: false,
      purchaseOrderNumber: false,
      invoiceNumber: false,
      invoiceDate: false,
      invoiceTotal: false,
      documentType: false,
    }
  }

  const finals = buildStructuredUploadFields(file)
  const expectedDocType = mapOcrDocumentTypeToUpload(
    suggested.documentTypeGuess,
  )

  return {
    supplier: Boolean(
      suggested.supplierName &&
        feedbackValuesEqual(suggested.supplierName, finals.supplierTitle),
    ),
    purchaseOrderNumber: Boolean(
      suggested.poNumber &&
        feedbackValuesEqual(suggested.poNumber, finals.purchaseOrderNumber),
    ),
    invoiceNumber: Boolean(
      suggested.invoiceNumber &&
        feedbackValuesEqual(suggested.invoiceNumber, finals.invoiceNumber),
    ),
    invoiceDate: Boolean(
      suggested.invoiceDate &&
        feedbackValuesEqual(
          normaliseInvoiceDateInput(suggested.invoiceDate),
          finals.invoiceDate,
        ),
    ),
    invoiceTotal: Boolean(
      suggested.grossTotal != null &&
        feedbackValuesEqual(suggested.grossTotal, finals.invoiceTotal),
    ),
    documentType: Boolean(
      suggested.documentTypeGuess && finals.documentType === expectedDocType,
    ),
  }
}

export function getAiFieldBadgeLabel(file = {}, field) {
  if (field === 'supplier' && isNonOmsSupplierUpload(file)) {
    return NON_OMS_SUPPLIER_BADGE_LABEL
  }
  const flags = getAiSuggestedFieldFlags(file)
  if (!file.ocrSuggested || file.ocrStatus !== 'suggested') {
    return null
  }
  // A total summed from line items is an estimate, not a read value.
  if (field === 'invoiceTotal' && isInvoiceTotalFromLineEstimate(file)) {
    return AI_ESTIMATED_BADGE_LABEL
  }
  if (flags[field]) {
    return AI_SUGGESTED_BADGE_LABEL
  }
  // Only show "Edited" when OCR had a suggestion for that field.
  const suggested = file.ocrSuggested
  const hadSuggestion = {
    supplier: suggested.supplierName != null && suggested.supplierName !== '',
    purchaseOrderNumber:
      suggested.poNumber != null && suggested.poNumber !== '',
    invoiceNumber:
      suggested.invoiceNumber != null && suggested.invoiceNumber !== '',
    invoiceDate: suggested.invoiceDate != null && suggested.invoiceDate !== '',
    invoiceTotal: suggested.grossTotal != null,
    documentType: Boolean(suggested.documentTypeGuess),
  }
  return hadSuggestion[field] ? AI_EDITED_BADGE_LABEL : null
}

/**
 * Closest OMS suppliers to show under the confirm-step supplier field.
 * Prefers OCR-time suggestions; falls back to live scoring on the current title.
 */
export function getOmsSupplierSuggestionsForUpload(
  file = {},
  venueSuppliers = [],
  { limit = 3 } = {},
) {
  if (isLikelyVenueSupplierId(file.supplier)) {
    return []
  }

  const cached = Array.isArray(file.omsSupplierSuggestions)
    ? file.omsSupplierSuggestions.filter(item => item?.id && item?.title)
    : []
  if (cached.length) {
    return cached.slice(0, limit)
  }

  const name =
    String(file.supplierTitle || '').trim() ||
    String(file.ocrSuggested?.supplierName || '').trim()
  if (!name) {
    return []
  }

  return suggestVenueSuppliersFromOcrName(name, venueSuppliers, { limit }).map(
    item => ({
      id: String(item.venueSupplier?._id || ''),
      title: item.venueSupplier?.supplierTitle || '',
      score: item.score,
    }),
  )
}

/**
 * Build structured fields for the enrich API after dashboard confirm.
 * Always stamps source=customer_dashboard and includes OCR field feedback.
 */
export function resolveInvoiceTotalSource(file = {}, structured = null) {
  const finals = structured || buildStructuredUploadFields(file)
  if (finals.invoiceTotal == null) {
    return null
  }
  if (isInvoiceTotalFromLineEstimate(file)) {
    return INVOICE_TOTAL_SOURCE.LINE_SUM
  }
  const grossTotal = file.ocrSuggested?.grossTotal
  if (grossTotal != null && grossTotal === finals.invoiceTotal) {
    return INVOICE_TOTAL_SOURCE.OCR
  }
  return INVOICE_TOTAL_SOURCE.MANUAL
}

export function buildEnrichPayloadFromUploadFile(file = {}) {
  const structured = buildStructuredUploadFields(file)
  const toNonNegativePennies = value => {
    if (value == null || value === '') return null
    const abs = Math.abs(Number(value))
    return Number.isFinite(abs) ? abs : null
  }

  const payload = {
    supplier: structured.supplier,
    supplierTitle: structured.supplierTitle,
    purchaseOrderNumber: structured.purchaseOrderNumber,
    linkedOrderId: structured.linkedOrderId,
    documentType: structured.documentType,
    comment: structured.comment,
    invoiceNumber: structured.invoiceNumber,
    invoiceDate: structured.invoiceDate,
    invoiceTotal: toNonNegativePennies(structured.invoiceTotal),
    invoiceTotalNet: toNonNegativePennies(file.ocrSuggested?.netTotal),
    invoiceVatTotal: toNonNegativePennies(file.ocrSuggested?.vatTotal),
    invoiceTotalSource: resolveInvoiceTotalSource(file, structured),
    source: 'customer_dashboard',
    // User-confirmed type wins over OCR. A delivery-note guess must not stick
    // when the user confirmed Invoice / Credit note on upload.
    detectedDocumentType:
      structured.documentType === 'creditNote' ? 'credit_note' : 'invoice',
    ocrFieldFeedback: buildUploadOcrFieldFeedback(file),
    supplierConfirmedByUser: true,
    uploadConfirmed: true,
  }

  // Drop undefined keys so the enrich body stays explicit.
  return Object.fromEntries(
    Object.entries(payload).filter(([, value]) => value !== undefined),
  )
}

export function applyPurchaseOrderUpdate(files, index, poUpdate) {
  return files.map((item, fileIndex) => {
    if (fileIndex !== index) {
      return item
    }

    const updated = {
      ...item,
      purchaseOrderNumber:
        poUpdate.purchaseOrderNumber ?? item.purchaseOrderNumber,
      linkedOrderId:
        poUpdate.linkedOrderId !== undefined
          ? poUpdate.linkedOrderId
          : item.linkedOrderId,
    }

    // Never silently overwrite / invent an OMS supplier when OCR already
    // printed a Non-OMS name (e.g. Fife Creamery). User must pick explicitly.
    const hasPrintedNonOmsName = isNonOmsSupplierUpload(item)
    if (!item.supplier && poUpdate.venueSupplierId && !hasPrintedNonOmsName) {
      updated.supplier = poUpdate.venueSupplierId
    }

    if (
      !item.supplierTitle &&
      poUpdate.supplierTitle &&
      !hasPrintedNonOmsName
    ) {
      updated.supplierTitle = poUpdate.supplierTitle
    }

    return updated
  })
}

/** Dashboard display labels for invoice.source (mirrors express reconciliation constants). */
export const INVOICE_SOURCE_LABELS = {
  customer_dashboard: 'Dashboard',
  customer_app: 'App',
  oms_admin: 'OMS Admin',
  email: 'Email',
  supplier_portal: 'Supplier Portal',
  edi_ironweb: 'EDI / IronWeb',
  other_integration: 'Other',
}

export const INVOICE_MATCHING_FAILURE_REASON_META = {
  po_not_found: {
    label: 'PO not found',
    guidance:
      'No OMS order matched that PO. For credit notes, the number may be an invoice reference — pick the correct purchase order in Apply.',
  },
  supplier_mismatch: {
    label: 'Supplier mismatch',
    guidance:
      'Supplier on the invoice doesn’t match the order — you can still match and reconcile. Confirm the supplier link if needed.',
  },
  total_outside_tolerance: {
    label: 'Total outside tolerance',
    guidance: 'Review the invoice and order totals before reconciling.',
  },
  po_already_fully_invoiced: {
    label: 'PO already fully invoiced',
    guidance:
      'This PO is already fully invoiced. Raising match tolerance will not help — review linked invoices, or unlink if this file belongs elsewhere.',
  },
  multiple_matches: {
    label: 'Multiple possible matches',
    guidance:
      'More than one order shares this PO. Choose the correct order, then Match & reconcile (or Link, then Finish reconcile).',
  },
  possible_duplicate: {
    label: 'Possible duplicate invoice',
    guidance: 'Review the existing invoice before continuing.',
  },
  order_already_reconciled: {
    label: 'Order already reconciled',
    guidance:
      'This order is already closed in finance (Sent to Xero). Review the existing reconciliation.',
  },
  missing_po: {
    label: 'No Order No on invoice',
    guidance:
      'This invoice has no purchase order / Order No (common on some supplier PDFs). Enter the OMS PO via Edit invoice details, or pick the order in Match — we can’t auto-match until a PO is set.',
  },
  missing_invoice_number: {
    label: 'Missing invoice number',
    guidance: 'Add the document number.',
  },
  missing_total: {
    label: 'Missing invoice total',
    guidance: 'Add the total including VAT.',
  },
  missing_supplier: {
    label: 'Supplier not linked',
    guidance:
      'No OMS supplier is linked. Keep the printed name as Non-OMS, or link the correct supplier under Your Suppliers — we won’t guess a different one.',
  },
  non_oms_supplier: {
    label: 'Not on Your Suppliers',
    guidance:
      'The name on this invoice isn’t an OMS supplier for this venue yet. Kept as Non-OMS — pick or add the right supplier under Your Suppliers; we won’t silently link another.',
  },
  covers_multiple_orders: {
    label: 'Covers multiple orders',
    guidance: 'Allocate the total across the relevant orders.',
  },
  looks_like_delivery_note: {
    label: 'Looks like a delivery note',
    guidance: 'Upload the invoice when available.',
  },
  credit_note_manual: {
    label: 'Credit note — apply to a PO',
    guidance:
      'This won’t match the PO total on its own — apply it to the PO. Credits offset over-billing; they do not replace an invoice.',
  },
  processing_error: {
    label: 'Reconciliation failed',
    guidance:
      'Retry Match & reconcile (safe), or finish Full accept on Goods Receipting.',
  },
  manual_review: {
    label: 'Manual review required',
    guidance: 'Review this invoice before reconciling.',
  },
}

export const INVOICE_RECONCILIATION_METHOD_LABELS = {
  manual: 'Manual',
  smart_match_one_click: 'Smart Match – One Click',
  smart_match_force: 'Smart Match – Outside tolerance',
  accepted_difference: 'Accepted difference',
  automatic_edi: 'Automatic – EDI',
  other_automatic: 'Other Automatic',
}

export function getInvoiceMatchingFailureReasonMeta(reason) {
  return (
    INVOICE_MATCHING_FAILURE_REASON_META[reason] || {
      label: reason
        ? String(reason)
            .replace(/_/g, ' ')
            .replace(/\b\w/g, letter => letter.toUpperCase())
        : '',
      guidance: '',
    }
  )
}

export function getInvoiceReconciliationMethodLabel(method) {
  if (!method) return 'Not reconciled yet'
  return INVOICE_RECONCILIATION_METHOD_LABELS[method] || method
}

/**
 * Hollow Tag presets — green border, light fill, coloured text.
 * Same visual language as chef-facing GR Confirmed / Receipted chips
 * (avoids antd solid white-on-colour Tags).
 */
export const HOLLOW_TAG_PRESETS = {
  green: {
    color: '#389e0d',
    background: '#f6ffed',
    borderColor: '#b7eb8f',
  },
  cyan: {
    color: '#08979c',
    background: '#e6fffb',
    borderColor: '#87e8de',
  },
  blue: {
    color: '#096dd9',
    background: '#e6f7ff',
    borderColor: '#91d5ff',
  },
  orange: {
    color: '#d46b08',
    background: '#fff7e6',
    borderColor: '#ffd591',
  },
  gold: {
    color: '#ad6800',
    background: '#fffbe6',
    borderColor: '#ffe58f',
  },
  volcano: {
    color: '#d4380d',
    background: '#fff2e8',
    borderColor: '#ffbb96',
  },
  red: {
    color: '#cf1322',
    background: '#fff1f0',
    borderColor: '#ffa39e',
  },
  purple: {
    color: '#531dab',
    background: '#f9f0ff',
    borderColor: '#d3adf7',
  },
  magenta: {
    color: '#c41d7f',
    background: '#fff0f6',
    borderColor: '#ffadd2',
  },
  default: {
    color: '#595959',
    background: '#fafafa',
    borderColor: '#d9d9d9',
  },
}

/**
 * Build antd Tag props for a hollow chip (no solid `color` prop).
 * @param {string} [preset] - key in HOLLOW_TAG_PRESETS or antd colour name
 */
export function getHollowTagProps(preset = 'default') {
  const style = HOLLOW_TAG_PRESETS[preset] || HOLLOW_TAG_PRESETS.default
  return {
    // Force hollow: never pass antd `color` (that paints solid white-on-colour).
    style: {
      color: style.color,
      background: style.background,
      borderColor: style.borderColor,
      borderStyle: 'solid',
    },
  }
}

/** Map matching tone → hollow Tag props (shared with Status / method chips). */
export function getHollowTagPropsForTone(tone = 'neutral') {
  const toneToPreset = {
    success: 'green',
    attention: 'orange',
    info: 'blue',
    duplicate: 'purple',
    neutral: 'default',
    danger: 'red',
  }
  return getHollowTagProps(toneToPreset[tone] || 'default')
}

/**
 * Matching-status badges for the invoices table.
 * Separate from lifecycle Status (Received / Reconciled / Sent to Xero).
 *
 * `tone` aligns with Goods receipting health chips/callouts
 * (success / attention / info / duplicate / neutral).
 */
export const INVOICE_MATCHING_STATUS_TONES = {
  success: {
    border: HOLLOW_TAG_PRESETS.green.borderColor,
    background: HOLLOW_TAG_PRESETS.green.background,
    color: HOLLOW_TAG_PRESETS.green.color,
    activeBorder: HOLLOW_TAG_PRESETS.green.borderColor,
    activeBackground: HOLLOW_TAG_PRESETS.green.background,
  },
  attention: {
    border: HOLLOW_TAG_PRESETS.orange.borderColor,
    background: HOLLOW_TAG_PRESETS.orange.background,
    color: HOLLOW_TAG_PRESETS.orange.color,
    activeBorder: HOLLOW_TAG_PRESETS.orange.borderColor,
    activeBackground: HOLLOW_TAG_PRESETS.orange.background,
  },
  info: {
    border: HOLLOW_TAG_PRESETS.blue.borderColor,
    background: HOLLOW_TAG_PRESETS.blue.background,
    color: HOLLOW_TAG_PRESETS.blue.color,
    activeBorder: HOLLOW_TAG_PRESETS.blue.borderColor,
    activeBackground: HOLLOW_TAG_PRESETS.blue.background,
  },
  duplicate: {
    border: HOLLOW_TAG_PRESETS.purple.borderColor,
    background: HOLLOW_TAG_PRESETS.purple.background,
    color: HOLLOW_TAG_PRESETS.purple.color,
    activeBorder: HOLLOW_TAG_PRESETS.purple.borderColor,
    activeBackground: HOLLOW_TAG_PRESETS.purple.background,
  },
  neutral: {
    border: HOLLOW_TAG_PRESETS.default.borderColor,
    background: HOLLOW_TAG_PRESETS.default.background,
    color: HOLLOW_TAG_PRESETS.default.color,
    activeBorder: '#8c8c8c',
    activeBackground: '#f5f5f5',
  },
  danger: {
    border: HOLLOW_TAG_PRESETS.red.borderColor,
    background: HOLLOW_TAG_PRESETS.red.background,
    color: HOLLOW_TAG_PRESETS.red.color,
    activeBorder: HOLLOW_TAG_PRESETS.red.borderColor,
    activeBackground: HOLLOW_TAG_PRESETS.red.background,
  },
}

export const INVOICE_MATCHING_STATUS_META = {
  unprocessed: {
    label: 'Unprocessed',
    tone: 'neutral',
    color: 'orange',
    guidance: 'Invoice uploaded and waiting to be matched.',
  },
  match_found: {
    label: 'Match found',
    tone: 'success',
    color: 'green',
    guidance:
      'We found a recommended order within tolerance. Open Match and choose Match & reconcile to confirm.',
  },
  partial_match: {
    label: 'Partial match',
    tone: 'attention',
    color: 'gold',
    guidance:
      'This invoice matches the PO but does not fully close the balance. Open Match to link it — more invoices are expected; finish on Goods Receipting.',
  },
  needs_action: {
    label: 'Review needed',
    tone: 'attention',
    color: 'volcano',
    guidance:
      'We could not confidently match this invoice. Open Match to choose a purchase order.',
  },
  possible_duplicate: {
    label: 'Possible duplicate',
    tone: 'duplicate',
    color: 'purple',
    guidance:
      'This may already exist in OMS. Review the existing invoice before matching.',
  },
  no_match: {
    label: 'No match',
    tone: 'neutral',
    // Do not use antd Tag color="default" — non-preset values get white text.
    color: null,
    guidance:
      'No matching OMS order was found. If ordered outside OMS, add a manual order on Orders first; if ordered in OMS, fix Goods receipting — then Match again.',
  },
  multiple_matches: {
    label: 'Multiple matches',
    tone: 'info',
    color: 'blue',
    guidance:
      'More than one order shares this PO. Open Match, choose the correct order, then Match & reconcile.',
  },
}

export const DEFAULT_MATCHING_STATUS = 'unprocessed'

export function getInvoiceMatchingStatusToneStyles(tone = 'neutral') {
  return (
    INVOICE_MATCHING_STATUS_TONES[tone] || INVOICE_MATCHING_STATUS_TONES.neutral
  )
}

export function getInvoiceSourceLabel(source) {
  if (!source) {
    return 'Unknown'
  }
  return INVOICE_SOURCE_LABELS[source] || source
}

export function getInvoiceMatchingStatusMeta(status) {
  return (
    INVOICE_MATCHING_STATUS_META[status] || {
      label: status || 'Unknown',
      tone: 'neutral',
      color: null,
      guidance: '',
    }
  )
}

/** Readable Tag styles for Matching column (hollow — same as Status / method). */
export function getInvoiceMatchingTagProps(status) {
  const meta = getInvoiceMatchingStatusMeta(status)
  return {
    meta,
    tagProps: getHollowTagPropsForTone(meta.tone),
  }
}

function isPerfectMatchSummary(matchSummary = {}) {
  const difference = matchSummary?.differencePennies
  if (difference == null || !Number.isFinite(Number(difference))) {
    return false
  }
  return Math.round(Number(difference)) === 0
}

function isAttachedLifecycle(invoice = {}) {
  return (invoice.status || '') === 'attachedToPO'
}

function isTotalsAlignedMatch(invoice = {}) {
  if (invoice.matchSummary?.oneClickEligible === true) return true
  if (invoice.matchingStatus === 'match_found') return true
  return isPerfectMatchSummary(invoice.matchSummary)
}

/**
 * Soft Matching-column presentation when lifecycle is Invoice attached.
 * Avoids scary “Review needed” on rows that are already on a PO; engine
 * detail stays in the tooltip. Returns null when no override applies.
 *
 * @returns {{ label: string, tone: string, guidance: string } | null}
 */
export function getInvoiceMatchingAttachedDisplayOverride(invoice = {}) {
  if (!isAttachedLifecycle(invoice)) return null

  const status = invoice.matchingStatus || DEFAULT_MATCHING_STATUS

  // Keep the failed-reconcile path — still needs a loud, actionable signal.
  if (
    status === 'needs_action' &&
    invoice.matchingFailureReason === 'processing_error'
  ) {
    return null
  }

  if (hasReconciledPoAllocation(invoice)) {
    const openPos = listOpenPoAllocations(invoice)
    if (openPos.length > 0) {
      const openPoNames = openPos.map(row => row.purchaseOrderNumber).join(', ')
      return {
        label: `Open on ${openPos.length} PO${openPos.length > 1 ? 's' : ''}`,
        tone: 'attention',
        guidance: `Partially reconciled — still open on ${openPoNames}. Use Match or Allocate to reconcile remaining POs.`,
      }
    }
  }

  if (isTotalsAlignedMatch(invoice)) {
    return {
      label: 'Ready to reconcile',
      tone: 'success',
      guidance:
        'Invoice is linked to a purchase order and totals align. Open Match and choose Match & reconcile, or Full accept on Goods Receipting.',
    }
  }

  if (status === 'partial_match') {
    return {
      label: 'Linked',
      tone: 'attention',
      guidance:
        'Invoice is linked but does not fully close the balance. Finish on Goods Receipting when remaining invoices arrive.',
    }
  }

  if (
    status === 'needs_action' ||
    status === 'no_match' ||
    status === 'multiple_matches' ||
    status === 'unprocessed'
  ) {
    return {
      label: 'Linked',
      tone: 'info',
      guidance:
        'Invoice is attached to a purchase order. Matching notes are in the tooltip — finish on Match or Goods Receipting.',
    }
  }

  return null
}

/**
 * Matching-column badge label for a row.
 * Uses the engine status, with a snappier “Perfect match” when totals are exact.
 * processing_error (linked-but-reconcile-failed) surfaces as Reconciliation failed.
 * When Invoice attached, softens Review needed → Linked / Ready to reconcile.
 */
export function getInvoiceMatchingDisplayLabel(invoice = {}) {
  const status = invoice.matchingStatus || DEFAULT_MATCHING_STATUS

  if (
    status === 'needs_action' &&
    invoice.matchingFailureReason === 'processing_error'
  ) {
    return 'Reconciliation failed'
  }

  // Step 2 — dual-photo offer (not Possible duplicate).
  if (
    invoice.matchSummary?.addPagesOffered &&
    invoice.matchSummary?.addPagesTargetInvoiceId &&
    status !== 'possible_duplicate'
  ) {
    return 'Add to existing?'
  }

  const attachedOverride = getInvoiceMatchingAttachedDisplayOverride(invoice)
  if (attachedOverride) {
    return attachedOverride.label
  }

  // Blank PO / supplier-not-on-venue — snappier than generic “Review needed”.
  if (status === 'needs_action') {
    const reason = String(invoice.matchingFailureReason || '').trim()
    if (
      reason === 'missing_po' ||
      reason === 'missing_supplier' ||
      reason === 'non_oms_supplier'
    ) {
      const failureLabel = getInvoiceMatchingFailureReasonMeta(reason).label
      if (failureLabel) return failureLabel
    }
  }

  const meta = getInvoiceMatchingStatusMeta(status)

  if (status === 'match_found' && isPerfectMatchSummary(invoice.matchSummary)) {
    return 'Perfect match'
  }

  return meta.label
}

/**
 * Tooltip / title for the Matching column badge, including remaining balance
 * for Partial Match rows when matchSummary has it.
 * Prefers engine matchingReasonDetail; attached rows fall back to soft guidance.
 */
export function getInvoiceMatchingBadgeTitle(invoice = {}) {
  const status = invoice.matchingStatus || DEFAULT_MATCHING_STATUS
  const meta = getInvoiceMatchingStatusMeta(status)
  const attachedOverride = getInvoiceMatchingAttachedDisplayOverride(invoice)
  const detail = String(invoice.matchingReasonDetail || '').trim()
  if (detail) {
    return detail
  }

  if (
    invoice.matchSummary?.addPagesOffered &&
    invoice.matchSummary?.addPagesTargetInvoiceId &&
    status !== 'possible_duplicate'
  ) {
    const number = String(
      invoice.matchSummary.addPagesTargetInvoiceNumber || '',
    ).trim()
    const target = number ? `#${number}` : 'an existing invoice'
    return `This looks like another photo of ${target}. Choose Add to existing invoice instead of treating it as a duplicate.`
  }

  if (attachedOverride?.guidance) {
    return attachedOverride.guidance
  }

  if (status === 'match_found' && isPerfectMatchSummary(invoice.matchSummary)) {
    const po =
      String(invoice.matchSummary?.purchaseOrderNumber || '').trim() ||
      String(invoice.purchaseOrderNumber || '').trim()
    return po
      ? `Exact totals for ${po}. Open Match to reconcile.`
      : 'Exact totals within tolerance. Open Match to reconcile.'
  }

  const remaining = invoice.matchSummary?.remainingBalancePennies
  if (
    status === 'partial_match' &&
    remaining != null &&
    Number.isFinite(Number(remaining))
  ) {
    const remainingDisplay = formatInvoiceTotalDisplay(Number(remaining))
    const orderDisplay = formatInvoiceTotalDisplay(
      invoice.matchSummary?.orderGrossPennies,
    )
    const linkedDisplay = formatInvoiceTotalDisplay(
      invoice.matchSummary?.linkedInvoiceTotalPennies ?? 0,
    )
    const orderWord =
      invoice.matchSummary?.orderGrossBasis === 'receipted'
        ? 'received'
        : invoice.matchSummary?.orderGrossBasis === 'ordered'
        ? 'ordered'
        : 'order'
    return `Partial match — ${linkedDisplay} of ${orderDisplay} ${orderWord} covered; ${remainingDisplay} still outstanding. Attach without closing the PO.`
  }

  return meta.guidance || undefined
}

/**
 * Tag styles for the Matching column. Prefer the full invoice so attached
 * overrides can soften tone (e.g. Review needed → Linked / info).
 * Perfect match and other chips use hollow Tags (no solid white-on-green).
 */
export function getInvoiceMatchingTagPropsForInvoice(invoice = {}) {
  const status = invoice.matchingStatus || DEFAULT_MATCHING_STATUS
  const attachedOverride = getInvoiceMatchingAttachedDisplayOverride(invoice)
  const meta = attachedOverride
    ? {
        ...getInvoiceMatchingStatusMeta(status),
        label: attachedOverride.label,
        tone: attachedOverride.tone,
        guidance: attachedOverride.guidance,
      }
    : getInvoiceMatchingStatusMeta(status)

  // Perfect match stays on the success (green hollow) tone.
  const label = getInvoiceMatchingDisplayLabel(invoice)
  let tone =
    label === 'Perfect match' || label === 'Ready to reconcile'
      ? 'success'
      : meta.tone

  if (label === 'Add to existing?') {
    tone = 'info'
  }

  return {
    meta: { ...meta, label, tone },
    tagProps: getHollowTagPropsForTone(tone),
  }
}

/** Options for matching-status filter chips / multi-select. */
export function getInvoiceMatchingStatusFilterOptions() {
  return Object.entries(INVOICE_MATCHING_STATUS_META).map(([value, meta]) => ({
    value,
    label: meta.label,
    color: meta.color,
    tone: meta.tone,
  }))
}

/**
 * Statuses that usually need customer attention (excludes plain Unprocessed).
 * Used by the Match queue “Needs my review” preset.
 */
export const INVOICE_MATCHING_WORK_STATUSES = [
  'match_found',
  'partial_match',
  'needs_action',
  'possible_duplicate',
  'no_match',
  'multiple_matches',
]

/** True when filters match the “Needs my review” preset (all work statuses). */
export function isInvoiceMatchingWorkPreset(statuses = []) {
  if (
    !Array.isArray(statuses) ||
    statuses.length !== INVOICE_MATCHING_WORK_STATUSES.length
  ) {
    return false
  }
  return INVOICE_MATCHING_WORK_STATUSES.every(status =>
    statuses.includes(status),
  )
}

/**
 * Clickable Match queue summary buckets (non-zero counts only in the UI).
 * Phrases are written to read after the count: "3 ready to reconcile".
 * `match_found` counts only invoices still open for reconcile (not attached /
 * reconciled / sent to Xero). Closed invoices use the lifecycle chip instead.
 */
export const INVOICE_MATCHING_SUMMARY_BUCKETS = [
  { status: 'match_found', phrase: 'ready to reconcile', tone: 'success' },
  { status: 'needs_action', phrase: 'review needed', tone: 'attention' },
  { status: 'partial_match', phrase: 'partial match', tone: 'attention' },
  {
    status: 'possible_duplicate',
    phrase: 'possible duplicate',
    tone: 'duplicate',
  },
  { status: 'no_match', phrase: 'no match', tone: 'info' },
  { status: 'multiple_matches', phrase: 'multiple matches', tone: 'info' },
]

/** Lifecycle summary chip — not a matching-status filter. */
export const INVOICE_LIFECYCLE_SUMMARY_BUCKETS = [
  { lifecycle: 'reconciled', phrase: 'reconciled', tone: 'success' },
]

/**
 * Invoices that are done for matching-queue work (reconciled / Xero).
 * Soft-attached (`attachedToPO`) stays in review/partial buckets but is not
 * “ready to reconcile”.
 */
export function isInvoiceClosedLifecycle(invoice = {}) {
  const lifecycle = invoice?.status || 'pending'
  return lifecycle === 'reconciled' || lifecycle === 'sentToXero'
}

/** Mirrors express `isReadyToReconcileInvoice` for Match queue chips. */
export function isInvoiceReadyToReconcile(invoice = {}) {
  if (invoice?.isArchived) return false
  if (isInvoiceClosedLifecycle(invoice)) return false
  const lifecycle = invoice?.status || 'pending'
  if (lifecycle === 'attachedToPO') return false
  return (invoice?.matchingStatus || DEFAULT_MATCHING_STATUS) === 'match_found'
}

export function countInvoicesByMatchingStatus(rows = []) {
  return (Array.isArray(rows) ? rows : []).reduce((acc, row) => {
    const status = row?.matchingStatus || DEFAULT_MATCHING_STATUS
    acc[status] = (acc[status] || 0) + 1
    return acc
  }, {})
}

/**
 * Matching-status counts for summary chips — excludes closed lifecycles so
 * reconciled “Perfect match” rows do not inflate “ready to reconcile”.
 */
export function countOpenInvoicesByMatchingStatus(rows = []) {
  return (Array.isArray(rows) ? rows : []).reduce((acc, row) => {
    if (isInvoiceClosedLifecycle(row)) return acc
    if (row?.isArchived) return acc
    const status = row?.matchingStatus || DEFAULT_MATCHING_STATUS
    if (status === 'match_found' && !isInvoiceReadyToReconcile(row)) {
      return acc
    }
    acc[status] = (acc[status] || 0) + 1
    return acc
  }, {})
}

export function countInvoicesByLifecycle(rows = [], lifecycle) {
  if (!lifecycle) return 0
  return (Array.isArray(rows) ? rows : []).filter(row => {
    if (row?.isArchived) return false
    return invoiceRowMatchesLifecycleStatuses(row, [lifecycle])
  }).length
}

export function getInvoiceMatchingWorkCount(rows = []) {
  const alertable = filterInvoicesInReconciliationAlertWindow(
    Array.isArray(rows) ? rows : [],
  )
  const counts = countOpenInvoicesByMatchingStatus(alertable)
  return INVOICE_MATCHING_WORK_STATUSES.reduce(
    (sum, status) => sum + (counts[status] || 0),
    0,
  )
}

export function buildInvoiceMatchingSummarySegments(rows = []) {
  const alertable = filterInvoicesInReconciliationAlertWindow(
    Array.isArray(rows) ? rows : [],
  )
  const matchingCounts = countOpenInvoicesByMatchingStatus(alertable)
  const matchingSegments = INVOICE_MATCHING_SUMMARY_BUCKETS.map(bucket => {
    const count = matchingCounts[bucket.status] || 0
    if (!count) return null
    return {
      kind: 'matching',
      status: bucket.status,
      count,
      tone: bucket.tone,
      label: `${count} ${bucket.phrase}`,
    }
  }).filter(Boolean)

  const lifecycleSegments = INVOICE_LIFECYCLE_SUMMARY_BUCKETS.map(bucket => {
    const count = countInvoicesByLifecycle(alertable, bucket.lifecycle)
    if (!count) return null
    return {
      kind: 'lifecycle',
      status: bucket.lifecycle,
      count,
      tone: bucket.tone,
      label: `${count} ${bucket.phrase}`,
    }
  }).filter(Boolean)

  return [...matchingSegments, ...lifecycleSegments]
}

export function invoiceRowMatchesSearch(row = {}, searchQuery = '') {
  const query = String(searchQuery || '')
    .trim()
    .toLowerCase()
  if (!query) {
    return true
  }

  const haystack = [
    row.invoiceNumber,
    row.purchaseOrderNumber,
    row.supplierTitle,
    row.fileName,
    row.notes,
    getInvoiceSourceLabel(row.source),
    getInvoiceMatchingStatusMeta(row.matchingStatus).label,
  ]
    .map(value => String(value || '').toLowerCase())
    .join(' ')

  return haystack.includes(query)
}

/**
 * Lifecycle Status column buckets (what the table shows), not raw DB enums.
 * Soft-attached (attachedToPO) mirrors GR “Invoice attached”; Reconciled is separate.
 */
export const INVOICE_LIFECYCLE_STATUS_FILTER_OPTIONS = [
  {
    value: 'received',
    label: 'Received',
    statuses: ['pending'],
  },
  {
    value: 'linked',
    label: 'Invoice attached',
    statuses: ['attachedToPO'],
  },
  {
    value: 'reconciled',
    label: 'Reconciled · Not in Xero',
    statuses: ['reconciled'],
  },
  {
    value: 'sentToXero',
    label: 'Sent to Xero',
    statuses: ['sentToXero'],
  },
  {
    value: 'error',
    label: 'Error - Cannot Read',
    statuses: ['error'],
  },
  {
    value: 'errorSendingToXero',
    label: 'Error Sending to Xero',
    statuses: ['errorSendingToXero'],
  },
]

/**
 * Resolve the PO number to show beside lifecycle Status when attached/reconciled.
 */
export function getInvoiceLifecyclePoNumber(invoice = {}) {
  const multiPo = getMultiPoAllocationSummary(invoice)
  if (multiPo.isMultiPo) {
    return multiPo.subtitle
  }

  const candidates = [
    invoice.purchaseOrderNumber,
    invoice.matchSummary?.purchaseOrderNumber,
    Array.isArray(invoice.matchedPurchaseOrderNumbers)
      ? invoice.matchedPurchaseOrderNumbers[0]
      : null,
    invoice.suggestedPurchaseOrderNumber,
  ]

  for (const value of candidates) {
    const trimmed = String(value || '').trim()
    if (trimmed) return trimmed
  }
  return ''
}

/**
 * Lifecycle Status display for the invoices table.
 * Soft-attach uses GR wording “Invoice attached”; multi-PO allocations show
 * “Allocated across N POs” with per-PO slices as the subtitle.
 * Tags use hollow presets (same language as Matching / GR Confirmed).
 */
export function getInvoiceLifecycleStatusDisplay(invoiceOrStatus = {}) {
  const invoice =
    typeof invoiceOrStatus === 'string'
      ? { status: invoiceOrStatus }
      : invoiceOrStatus || {}
  const status = invoice.status || 'pending'
  const multiPo = getMultiPoAllocationSummary(invoice)
  const purchaseOrderNumber = getInvoiceLifecyclePoNumber(invoice)

  if (status === 'attachedToPO') {
    const isCreditNote =
      typeof invoiceOrStatus === 'object' &&
      (invoice.documentType === 'creditNote' ||
        String(invoice.detectedDocumentType || '')
          .toLowerCase()
          .includes('credit'))
    return {
      label: multiPo.isMultiPo
        ? multiPo.label
        : isCreditNote
        ? 'Applied'
        : 'Invoice attached',
      tagColor: isCreditNote ? 'purple' : 'cyan',
      tagProps: getHollowTagProps(isCreditNote ? 'purple' : 'cyan'),
      purchaseOrderNumber,
      allocationSummary: multiPo,
    }
  }
  if (status === 'reconciled') {
    return {
      label: 'Reconciled · Not in Xero',
      tagColor: 'green',
      tagProps: getHollowTagProps('green'),
      purchaseOrderNumber,
      allocationSummary: multiPo,
    }
  }
  if (status === 'sentToXero') {
    return {
      label: 'Sent to Xero',
      tagColor: 'blue',
      tagProps: getHollowTagProps('blue'),
      purchaseOrderNumber,
      allocationSummary: multiPo,
    }
  }
  if (status === 'error') {
    return {
      label: 'Error - Cannot Read',
      tagColor: 'red',
      tagProps: getHollowTagProps('red'),
      purchaseOrderNumber: '',
    }
  }
  if (status === 'errorSendingToXero') {
    return {
      label: 'Error Sending to Xero',
      tagColor: 'red',
      tagProps: getHollowTagProps('red'),
      purchaseOrderNumber: '',
    }
  }
  return {
    label: 'Received',
    tagColor: 'orange',
    tagProps: getHollowTagProps('orange'),
    purchaseOrderNumber: '',
  }
}

/** Hollow Tag props for Reconciliation method chips. */
export function getInvoiceReconciliationMethodTagProps(method) {
  if (!method) {
    return getHollowTagProps('default')
  }
  return getHollowTagProps('green')
}

export function getInvoiceLifecycleStatusFilterOptions() {
  return INVOICE_LIFECYCLE_STATUS_FILTER_OPTIONS.map(({ value, label }) => ({
    value,
    label,
  }))
}

export function getInvoiceSourceFilterOptions() {
  return Object.entries(INVOICE_SOURCE_LABELS).map(([value, label]) => ({
    value,
    label,
  }))
}

/** One-line triage cue when the Source filter is App-only (D1). */
export const INVOICE_APP_SOURCE_FILTER_HINT =
  'Showing invoices uploaded from the app.'

export function getInvoiceAppSourceFilterHint(sources = []) {
  const selected = Array.isArray(sources) ? sources.filter(Boolean) : []
  if (selected.length === 1 && selected[0] === 'customer_app') {
    return INVOICE_APP_SOURCE_FILTER_HINT
  }
  return null
}

export function getInvoiceLifecycleStatusFilterValue(status) {
  const normalised = status || 'pending'
  const option = INVOICE_LIFECYCLE_STATUS_FILTER_OPTIONS.find(item =>
    item.statuses.includes(normalised),
  )
  return option?.value || 'received'
}

export function invoiceRowMatchesLifecycleStatuses(
  row = {},
  lifecycleStatuses = [],
) {
  const selected = Array.isArray(lifecycleStatuses)
    ? lifecycleStatuses.filter(Boolean)
    : []
  if (!selected.length) {
    return true
  }
  return selected.includes(getInvoiceLifecycleStatusFilterValue(row.status))
}

export function invoiceRowMatchesSources(row = {}, sources = []) {
  const selected = Array.isArray(sources) ? sources.filter(Boolean) : []
  if (!selected.length) {
    return true
  }
  // Unknown / missing source is only included when explicitly requested.
  if (!row.source) {
    return selected.includes('unknown')
  }
  return selected.includes(row.source)
}

/**
 * Inclusive calendar-day range on uploadedDate.
 * Pass null/empty bounds to skip that side of the range.
 * Accepts Date, ISO string, timestamp, or moment-like objects.
 */
export function invoiceRowMatchesUploadDateRange(
  row = {},
  uploadDateFrom = null,
  uploadDateTo = null,
) {
  if (uploadDateFrom == null && uploadDateTo == null) {
    return true
  }
  if (!row.uploadedDate) {
    return false
  }

  const uploadedMs = toDateMs(row.uploadedDate)
  if (uploadedMs == null) {
    return false
  }

  if (uploadDateFrom != null) {
    const fromMs = toStartOfDayMs(uploadDateFrom)
    if (fromMs != null && uploadedMs < fromMs) {
      return false
    }
  }

  if (uploadDateTo != null) {
    const toMs = toEndOfDayMs(uploadDateTo)
    if (toMs != null && uploadedMs > toMs) {
      return false
    }
  }

  return true
}

function toDateMs(value) {
  if (value == null || value === '') {
    return null
  }
  // Moment / Dayjs / Date expose a numeric valueOf(); plain strings do not.
  if (typeof value !== 'string' && typeof value?.valueOf === 'function') {
    const ms = Number(value.valueOf())
    if (Number.isFinite(ms)) {
      return ms
    }
  }
  const ms = new Date(value).getTime()
  return Number.isFinite(ms) ? ms : null
}

function toStartOfDayMs(value) {
  const ms = toDateMs(value)
  if (ms == null) return null
  const date = new Date(ms)
  date.setHours(0, 0, 0, 0)
  return date.getTime()
}

function toEndOfDayMs(value) {
  const ms = toDateMs(value)
  if (ms == null) return null
  const date = new Date(ms)
  date.setHours(23, 59, 59, 999)
  return date.getTime()
}

/**
 * Client-side invoices table filter:
 * matching statuses + lifecycle status + source + upload date range +
 * download status + search.
 * Empty arrays / null date bounds / 'all' downloadStatus mean “no filter”.
 */
export function filterInvoiceTableRows(
  rows = [],
  {
    matchingStatuses = [],
    lifecycleStatuses = [],
    sources = [],
    uploadDateFrom = null,
    uploadDateTo = null,
    downloadStatus = 'all',
    searchQuery = '',
  } = {},
) {
  const selectedMatching = Array.isArray(matchingStatuses)
    ? matchingStatuses.filter(Boolean)
    : []
  const selectedLifecycle = Array.isArray(lifecycleStatuses)
    ? lifecycleStatuses.filter(Boolean)
    : []
  const selectedSources = Array.isArray(sources) ? sources.filter(Boolean) : []

  return rows.filter(row => {
    if (selectedMatching.length) {
      if (!selectedMatching.includes(row.matchingStatus)) {
        return false
      }
      // Matching-type filters alone are a work queue — hide closed lifecycles
      // unless Status is also filtering (e.g. Reconciled + Match found).
      if (!selectedLifecycle.length) {
        if (isInvoiceClosedLifecycle(row)) {
          return false
        }
        if (
          row.matchingStatus === 'match_found' &&
          !isInvoiceReadyToReconcile(row)
        ) {
          return false
        }
      }
    }
    if (
      selectedLifecycle.length &&
      !invoiceRowMatchesLifecycleStatuses(row, selectedLifecycle)
    ) {
      return false
    }
    if (
      selectedSources.length &&
      !invoiceRowMatchesSources(row, selectedSources)
    ) {
      return false
    }
    if (!invoiceRowMatchesUploadDateRange(row, uploadDateFrom, uploadDateTo)) {
      return false
    }
    if (!invoiceRowMatchesDownloadStatus(row, downloadStatus)) {
      return false
    }
    return invoiceRowMatchesSearch(row, searchQuery)
  })
}

/** Whether the invoice has ever been downloaded (first-download fields). */
export function isInvoiceDownloaded(row = {}) {
  return Boolean(row.downloaded || row.downloadedAt)
}

/**
 * Download status filter: 'all' | 'downloaded' | 'not_downloaded'.
 * Default / unknown values do not filter (safe for existing callers).
 */
export function invoiceRowMatchesDownloadStatus(row, downloadStatus = 'all') {
  if (!downloadStatus || downloadStatus === 'all') {
    return true
  }
  const downloaded = isInvoiceDownloaded(row)
  if (downloadStatus === 'downloaded') {
    return downloaded
  }
  if (downloadStatus === 'not_downloaded') {
    return !downloaded
  }
  return true
}

export const INVOICE_DOWNLOAD_STATUS_FILTER_OPTIONS = [
  { value: 'all', label: 'All' },
  { value: 'not_downloaded', label: 'Not downloaded' },
  { value: 'downloaded', label: 'Downloaded' },
]

export function getInvoiceDownloadStatusFilterOptions() {
  return INVOICE_DOWNLOAD_STATUS_FILTER_OPTIONS
}

export function toggleMatchingStatusFilter(selectedStatuses = [], status) {
  if (!status) {
    return selectedStatuses
  }
  if (selectedStatuses.includes(status)) {
    return selectedStatuses.filter(value => value !== status)
  }
  return [...selectedStatuses, status]
}

export function toggleFilterValue(selectedValues = [], value) {
  if (!value) {
    return selectedValues
  }
  if (selectedValues.includes(value)) {
    return selectedValues.filter(item => item !== value)
  }
  return [...selectedValues, value]
}

/**
 * Whether any table filter (matching, lifecycle, source, date, download, search) is active.
 */
export function hasActiveInvoiceTableFilters({
  matchingStatuses = [],
  lifecycleStatuses = [],
  sources = [],
  uploadDateFrom = null,
  uploadDateTo = null,
  downloadStatus = 'all',
  searchQuery = '',
} = {}) {
  return (
    (Array.isArray(matchingStatuses) && matchingStatuses.length > 0) ||
    (Array.isArray(lifecycleStatuses) && lifecycleStatuses.length > 0) ||
    (Array.isArray(sources) && sources.length > 0) ||
    uploadDateFrom != null ||
    uploadDateTo != null ||
    (downloadStatus && downloadStatus !== 'all') ||
    Boolean(String(searchQuery || '').trim())
  )
}

/** Format pennies for the invoices table (e.g. £51.95). */
export function formatInvoiceTotalDisplay(pennies) {
  if (pennies == null || pennies === '') {
    return '—'
  }
  const formatted = formatPenniesAsPoundsInput(pennies)
  if (!formatted) {
    return '—'
  }
  return formatted.startsWith('-') ? `-£${formatted.slice(1)}` : `£${formatted}`
}

/** Clearer empty-state for supplier column. */
export function formatSupplierTitleDisplay(supplierTitle) {
  const title = String(supplierTitle || '').trim()
  return title || 'No supplier'
}

/**
 * Engine-scored recommended PO only.
 * Never falls back to the invoice’s typed/OCR PO field — that is not a match.
 */
export function getEngineRecommendedPurchaseOrderNumber(invoice = {}) {
  const matched = Array.isArray(invoice.matchedPurchaseOrderNumbers)
    ? invoice.matchedPurchaseOrderNumbers
        .map(value => String(value || '').trim())
        .filter(Boolean)
    : []
  if (matched[0]) {
    return matched[0]
  }

  return String(invoice.matchSummary?.purchaseOrderNumber || '').trim()
}

/** PO printed / typed on the invoice document (not an engine recommendation). */
export function getInvoiceDocumentPurchaseOrderNumber(invoice = {}) {
  return String(invoice.purchaseOrderNumber || '').trim()
}

/**
 * Suggested PO from smart-match fields for quick-link / Match recommend.
 * Engine-scored only — never the invoice PO field alone.
 */
export function getSuggestedPurchaseOrderNumber(invoice = {}) {
  return getEngineRecommendedPurchaseOrderNumber(invoice)
}

/** Confirmed is a typed or linked PO. Suggested is the engine recommendation. */
export function getInvoicePurchaseOrderLines(invoice = {}) {
  const manual = String(invoice.manualPurchaseOrderNumber || '').trim()
  const documentPo = String(invoice.purchaseOrderNumber || '').trim()
  const rejected = new Set(
    (Array.isArray(invoice.rejectedPurchaseOrderNumbers)
      ? invoice.rejectedPurchaseOrderNumbers
      : []
    )
      .map(value => String(value || '').trim())
      .filter(Boolean),
  )
  const linked =
    Boolean(invoice.linkedOrderId) ||
    ['attachedToPO', 'reconciled', 'sentToXero'].includes(invoice.status)
  const confirmed = manual || (linked ? documentPo : '')
  let suggested = String(getSuggestedPurchaseOrderNumber(invoice) || '').trim()
  if (!suggested || suggested === confirmed || rejected.has(suggested)) {
    suggested = ''
  }
  return { confirmed, suggested, documentPo }
}

export function getSuggestedLinkedOrderId(invoice = {}) {
  if (invoice.linkedOrderId) {
    return String(invoice.linkedOrderId)
  }
  const matched = Array.isArray(invoice.matchedOrderIds)
    ? invoice.matchedOrderIds.map(id => String(id || '')).filter(Boolean)
    : []
  return matched[0] || null
}

/**
 * Review Difference can open the receipt when the invoice is linked to an order.
 * Suggestions alone do not count.
 */
export function canEditGoodsReceiptFromReview(invoice = {}) {
  return getInvoiceViewOrderPath('venue', invoice) !== ''
}

/**
 * Path to the existing goods-receipt order view for a confirmed link.
 * Suggestions alone do not count.
 */
export function getInvoiceViewOrderPath(venueId, invoice = {}) {
  if (!venueId) return ''
  const allocations = Array.isArray(invoice.poAllocations)
    ? invoice.poAllocations
    : []
  const allocationPo = String(
    allocations.find(
      slice => slice?.orderId && String(slice.purchaseOrderNumber || '').trim(),
    )?.purchaseOrderNumber || '',
  ).trim()
  const po = String(invoice.purchaseOrderNumber || '').trim() || allocationPo
  if (!po) return ''
  const lifecycle = invoice.status || ''
  const confirmed =
    Boolean(invoice.linkedOrderId) ||
    Boolean(allocationPo) ||
    lifecycle === 'attachedToPO' ||
    lifecycle === 'reconciled' ||
    lifecycle === 'sentToXero'
  if (!confirmed) return ''
  return `/v/${venueId}/purchasing/receipting?q=${encodeURIComponent(po)}`
}

export function isInvoiceSoftLinkedToPo(invoice = {}) {
  return Boolean(
    invoice.linkedOrderId || String(invoice.purchaseOrderNumber || '').trim(),
  )
}

/** True when there is a suggestion the user can one-click soft-link. */
export function canQuickLinkSuggestedPo(invoice = {}) {
  const lifecycle = invoice.status || 'pending'
  if (
    lifecycle === 'attachedToPO' ||
    lifecycle === 'reconciled' ||
    lifecycle === 'sentToXero'
  ) {
    return false
  }

  const suggestedPo = getSuggestedPurchaseOrderNumber(invoice)
  if (!suggestedPo) {
    return false
  }

  const currentPo = String(invoice.purchaseOrderNumber || '').trim()
  const suggestedOrderId = getSuggestedLinkedOrderId(invoice)
  const alreadyLinked =
    currentPo === suggestedPo &&
    (!suggestedOrderId ||
      String(invoice.linkedOrderId || '') === String(suggestedOrderId))

  // Allow quick link when suggestion exists and either not soft-linked yet,
  // or soft-linked fields are incomplete (PO without order id).
  if (!isInvoiceSoftLinkedToPo(invoice)) {
    return true
  }

  return !alreadyLinked || (currentPo === suggestedPo && !invoice.linkedOrderId)
}

export function canOpenLinkPoModal(invoice = {}) {
  const lifecycle = invoice.status || 'pending'
  return !(
    lifecycle === 'attachedToPO' ||
    lifecycle === 'reconciled' ||
    lifecycle === 'sentToXero'
  )
}

/**
 * Edit invoice details (Actions pencil → modal). Locked after reconcile / Xero
 * / archive — same spirit as Remove from this PO.
 */
export function getEditInvoiceDetailsBlockReason(invoice = {}) {
  if (invoice?.isArchived) {
    return 'Archived invoices cannot be edited.'
  }
  const lifecycle = invoice.status || 'pending'
  if (lifecycle === 'sentToXero') {
    return 'This invoice has been sent to Xero and cannot be edited here.'
  }
  if (lifecycle === 'reconciled') {
    return 'This invoice is reconciled and cannot be edited here.'
  }
  return ''
}

export function canEditInvoiceDetails(invoice = {}) {
  return !getEditInvoiceDetailsBlockReason(invoice)
}

/**
 * Action meta for the Edit pencil (show disabled + clear tooltip when locked).
 */
export function getEditInvoiceDetailsActionMeta(invoice = {}) {
  const blockReason = getEditInvoiceDetailsBlockReason(invoice)
  const canEdit = !blockReason
  return {
    canEdit,
    // Hide on archive list; on the live list show a disabled pencil when locked
    // so chefs get clear messaging (same protection as Remove).
    canShow: !invoice?.isArchived,
    tooltip: canEdit ? 'Edit invoice details' : blockReason,
    blockReason,
  }
}

/**
 * Manual PO pencil edit is for pre-attach correction only.
 * Once soft-attached / reconciled / sent to Xero, change PO via Unlink first
 * (same gate as Link PO modal). Matching engine status alone does not lock.
 * @deprecated Prefer Edit invoice details modal (canEditInvoiceDetails).
 */
export function canEditInvoicePurchaseOrder(invoice = {}) {
  return canOpenLinkPoModal(invoice)
}

/**
 * Prefer a real person name / email for Uploaded By.
 * Falls back to legacy username; never collapses OMS uploads to bare "Admin"
 * when uploadedByName is present.
 */
export function getInvoiceUploadedByDisplay(invoice = {}) {
  if (invoice?.supplierUpload) {
    return (
      String(invoice.uploadedByName || invoice.username || '').trim() ||
      'Supplier portal'
    )
  }

  const candidates = [
    invoice.uploadedByName,
    invoice.username,
    invoice.uploadedByEmail,
    invoice.emailAddress,
  ]

  for (const value of candidates) {
    const trimmed = String(value || '').trim()
    if (!trimmed) continue
    if (
      trimmed.toLowerCase() === 'admin' ||
      trimmed.toLowerCase() === 'system'
    ) {
      continue
    }
    return trimmed
  }

  if (invoice.userType === 'Admin' || invoice.uploadedByType === 'oms') {
    return 'OMS Admin'
  }

  return '—'
}

export function invoiceForwardRowNote(attempts = []) {
  const latest = Array.isArray(attempts) ? attempts[attempts.length - 1] : null
  if (!latest) return ''
  if (latest.status === 'sent') {
    const to = (latest.to || []).filter(Boolean).join(', ')
    return to ? `Emailed to ${to}` : 'Emailed'
  }
  return 'This invoice could not be emailed.'
}

function formatForwardSentDay(value) {
  const date = new Date(value)
  if (Number.isNaN(date.getTime())) return ''
  return new Intl.DateTimeFormat('en-GB', {
    day: 'numeric',
    month: 'short',
    timeZone: 'Europe/London',
  }).format(date)
}

export function invoiceForwardSentHistory(attempts = []) {
  if (!Array.isArray(attempts)) return []
  return attempts
    .filter(attempt => attempt?.status === 'sent')
    .map(attempt => {
      const when = formatForwardSentDay(attempt.at)
      const to = (attempt.to || []).filter(Boolean).join(', ')
      return ['Sent', when, to ? `to ${to}` : ''].filter(Boolean).join(' ')
    })
}

export function invoiceForwardSentLine(at, to = []) {
  return invoiceForwardSentHistory([{ status: 'sent', at, to }])[0] || 'Sent'
}

export function mapInvoiceToTableRow(invoice) {
  const invoiceUrl =
    invoice.invoiceTempUrl ||
    invoice.invoiceUrl ||
    invoice.files?.[0]?.url ||
    ''
  const isSupplierUpload = Boolean(invoice.supplierUpload)
  const matchedOrderIds = Array.isArray(invoice.matchedOrderIds)
    ? invoice.matchedOrderIds.map(id => String(id))
    : []
  const matchedPurchaseOrderNumbers = Array.isArray(
    invoice.matchedPurchaseOrderNumbers,
  )
    ? invoice.matchedPurchaseOrderNumbers
        .map(value => String(value || '').trim())
        .filter(Boolean)
    : []

  const row = {
    id: invoice._id,
    checked: invoice.checked,
    uploadedDate: invoice.uploadedDate,
    fileName: invoice?.key?.split('/').pop() || invoice.originalFilename || '',
    storageKey: invoice.key || '',
    forwardNote:
      invoice.forwardNote || invoiceForwardRowNote(invoice.forwardAttempts),
    forwardHistory:
      invoice.forwardHistory ||
      invoiceForwardSentHistory(invoice.forwardAttempts),
    supplierTitle: invoice.supplierTitle || '',
    supplierId: invoice.supplier || '',
    purchaseOrderNumber: invoice.purchaseOrderNumber || '',
    manualPurchaseOrderNumber: String(
      invoice.manualPurchaseOrderNumber || '',
    ).trim(),
    rejectedPurchaseOrderNumbers: Array.isArray(
      invoice.rejectedPurchaseOrderNumbers,
    )
      ? invoice.rejectedPurchaseOrderNumbers
      : [],
    linkedOrderId: invoice.linkedOrderId ? String(invoice.linkedOrderId) : null,
    softLinkedInvoiceId: invoice.softLinkedInvoiceId
      ? String(invoice.softLinkedInvoiceId)
      : null,
    matchedOrderIds,
    matchedPurchaseOrderNumbers,
    poAllocations: Array.isArray(invoice.poAllocations)
      ? invoice.poAllocations.map(allocation => ({
          purchaseOrderNumber: String(
            allocation?.purchaseOrderNumber || '',
          ).trim(),
          amountPennies:
            allocation?.amountPennies == null
              ? null
              : Number(allocation.amountPennies),
          orderId: allocation?.orderId ? String(allocation.orderId) : null,
          reconciledAt: allocation?.reconciledAt || null,
        }))
      : [],
    creditDiscrepancy: invoice.creditDiscrepancy || null,
    differenceAcceptance: invoice.differenceAcceptance
      ? {
          acceptedAt: invoice.differenceAcceptance.acceptedAt || null,
          acceptedByName: invoice.differenceAcceptance.acceptedByName || '',
          differencePennies:
            invoice.differenceAcceptance.differencePennies == null
              ? null
              : Number(invoice.differenceAcceptance.differencePennies),
          invoiceTotalPennies:
            invoice.differenceAcceptance.invoiceTotalPennies == null
              ? null
              : Number(invoice.differenceAcceptance.invoiceTotalPennies),
          receivedGrossPennies:
            invoice.differenceAcceptance.receivedGrossPennies == null
              ? null
              : Number(invoice.differenceAcceptance.receivedGrossPennies),
          tolerancePennies:
            invoice.differenceAcceptance.tolerancePennies == null
              ? null
              : Number(invoice.differenceAcceptance.tolerancePennies),
          notes: invoice.differenceAcceptance.notes || '',
        }
      : null,
    matchingReasonDetail: invoice.matchingReasonDetail || '',
    matchSummary: invoice.matchSummary
      ? {
          purchaseOrderNumber: invoice.matchSummary.purchaseOrderNumber || '',
          invoiceGrossPennies:
            invoice.matchSummary.invoiceGrossPennies == null
              ? null
              : Number(invoice.matchSummary.invoiceGrossPennies),
          orderGrossPennies:
            invoice.matchSummary.orderGrossPennies == null
              ? null
              : Number(invoice.matchSummary.orderGrossPennies),
          orderGrossBasis:
            invoice.matchSummary.orderGrossBasis === 'receipted' ||
            invoice.matchSummary.orderGrossBasis === 'ordered'
              ? invoice.matchSummary.orderGrossBasis
              : null,
          catchWeightPriceLabel: String(
            invoice.matchSummary.catchWeightPriceLabel || '',
          ).trim(),
          differencePennies:
            invoice.matchSummary.differencePennies == null
              ? null
              : Number(invoice.matchSummary.differencePennies),
          linkedInvoiceTotalPennies:
            invoice.matchSummary.linkedInvoiceTotalPennies == null
              ? null
              : Number(invoice.matchSummary.linkedInvoiceTotalPennies),
          remainingBalancePennies:
            invoice.matchSummary.remainingBalancePennies == null
              ? null
              : Number(invoice.matchSummary.remainingBalancePennies),
          tolerancePennies:
            invoice.matchSummary.tolerancePennies == null
              ? null
              : Number(invoice.matchSummary.tolerancePennies),
          oneClickEligible: Boolean(invoice.matchSummary.oneClickEligible),
          evaluatedAt: invoice.matchSummary.evaluatedAt || null,
          duplicatePrevented: Boolean(invoice.matchSummary.duplicatePrevented),
          duplicateMatchCount: Number(
            invoice.matchSummary.duplicateMatchCount || 0,
          ),
          duplicateConfidence: invoice.matchSummary.duplicateConfidence || null,
          duplicateMatchInvoiceIds: Array.isArray(
            invoice.matchSummary.duplicateMatchInvoiceIds,
          )
            ? invoice.matchSummary.duplicateMatchInvoiceIds.map(id =>
                String(id),
              )
            : [],
          addPagesOffered: Boolean(invoice.matchSummary.addPagesOffered),
          addPagesTargetInvoiceId: invoice.matchSummary.addPagesTargetInvoiceId
            ? String(invoice.matchSummary.addPagesTargetInvoiceId)
            : null,
          addPagesTargetInvoiceNumber:
            invoice.matchSummary.addPagesTargetInvoiceNumber || null,
          addPagesReason: invoice.matchSummary.addPagesReason || null,
          addPagesConfidence: invoice.matchSummary.addPagesConfidence || null,
          addPagesMatchInvoiceIds: Array.isArray(
            invoice.matchSummary.addPagesMatchInvoiceIds,
          )
            ? invoice.matchSummary.addPagesMatchInvoiceIds.map(id => String(id))
            : [],
        }
      : null,
    documentType: invoice.documentType || DEFAULT_DOCUMENT_TYPE,
    invoiceNumber: invoice.invoiceNumber || '',
    invoiceDate: invoice.invoiceDate || null,
    invoiceTotal:
      invoice.invoiceTotal == null || invoice.invoiceTotal === ''
        ? null
        : Number(invoice.invoiceTotal),
    source: invoice.source || null,
    matchingStatus: invoice.matchingStatus || DEFAULT_MATCHING_STATUS,
    matchingFailureReason: invoice.matchingFailureReason || null,
    reconciliationMethod: invoice.reconciliationMethod || null,
    reconciledByName: invoice.reconciledByName || '',
    reconciledAt: invoice.reconciledAt || null,
    auditHistory: Array.isArray(invoice.auditHistory)
      ? invoice.auditHistory.map(entry => ({
          timestamp: entry?.timestamp || null,
          event: entry?.event || '',
          actor: entry?.actor || '',
          actorType: entry?.actorType || '',
          detail: entry?.detail || '',
          dataSnapshot: entry?.dataSnapshot || null,
        }))
      : [],
    permanentDeleteAt: invoice.permanentDeleteAt || null,
    notes: invoice.comment || invoice.notes || '',
    invoiceUrl,
    invoiceTempUrl: invoice.invoiceTempUrl || '',
    additionalPages: Array.isArray(invoice.additionalPages)
      ? invoice.additionalPages.map(page => ({
          key: page?.key || '',
          invoiceUrl: page?.invoiceUrl || '',
          invoiceTempUrl: page?.invoiceTempUrl || '',
          originalFilename: page?.originalFilename || '',
          uploadedDate: page?.uploadedDate || null,
          sourceInvoiceId: page?.sourceInvoiceId
            ? String(page.sourceInvoiceId)
            : null,
        }))
      : [],
    pageCount: Math.max(
      1,
      Number(invoice.pageCount) > 0
        ? Number(invoice.pageCount)
        : 1 +
            (Array.isArray(invoice.additionalPages)
              ? invoice.additionalPages.length
              : 0),
    ),
    username: getInvoiceUploadedByDisplay({
      ...invoice,
      supplierUpload: isSupplierUpload,
    }),
    uploadedByName: invoice.uploadedByName || '',
    downloaded: Boolean(invoice.downloaded || invoice.downloadedAt),
    downloadedAt: invoice.downloadedAt || null,
    downloadedByName: String(invoice.downloadedByName || '').trim(),
    downloadHistory: Array.isArray(invoice.downloadHistory)
      ? invoice.downloadHistory
      : [],
    status: invoice.status || 'pending',
    supplierUpload: isSupplierUpload,
    isLinkedToPO: Boolean(invoice.isLinkedToPO),
    ocrPurchaseOrderCandidates: getInvoiceOcrPurchaseOrderCandidates(invoice),
  }

  row.suggestedPurchaseOrderNumber = getSuggestedPurchaseOrderNumber(row)
  row.suggestedLinkedOrderId = getSuggestedLinkedOrderId(row)

  return row
}

export function getInvoiceStorageKey(invoice = {}) {
  return invoice.storageKey || invoice.key || ''
}

export function resolveInvoiceNumberForAttach(invoice = {}, order = {}) {
  if (invoice.invoiceNumber?.trim()) {
    return invoice.invoiceNumber.trim()
  }

  if (invoice.purchaseOrderNumber?.trim()) {
    return invoice.purchaseOrderNumber.trim()
  }

  if (order?.purchaseOrderNumber?.trim()) {
    return order.purchaseOrderNumber.trim()
  }

  if (invoice.fileName?.trim()) {
    return invoice.fileName.replace(/\.[^.]+$/, '').trim()
  }

  const storageKey = getInvoiceStorageKey(invoice)
  if (storageKey) {
    const fileNameFromKey = storageKey.split('/').pop() || ''
    return fileNameFromKey.replace(/\.[^.]+$/, '').trim()
  }

  return ''
}

export function resolveInvoiceDateForAttach(invoice = {}, order = {}) {
  if (invoice.invoiceDate) {
    return invoice.invoiceDate
  }

  if (invoice.uploadedDate) {
    return invoice.uploadedDate
  }

  if (order?.invoiceDate) {
    return order.invoiceDate
  }

  if (order?.orderedDate) {
    return order.orderedDate
  }

  return null
}

export function getInvoicePreviewUrl(invoice = {}, expressBaseUrl = '') {
  if (invoice.invoiceTempUrl) {
    return invoice.invoiceTempUrl
  }

  const storageKey = getInvoiceStorageKey(invoice)
  if (storageKey && expressBaseUrl) {
    return `${expressBaseUrl}files/${encodeURIComponent(storageKey)}`
  }

  return invoice.invoiceUrl || ''
}

export function isInvoicePdfFile(invoice = {}) {
  const candidates = [
    invoice.storageKey,
    invoice.key,
    invoice.fileName,
    invoice.invoiceUrl,
    invoice.invoiceTempUrl,
  ]

  return candidates.some(value =>
    String(value || '')
      .toLowerCase()
      .includes('.pdf'),
  )
}

export function buildEditInvoiceState(editInvoice = {}) {
  const totalPennies =
    editInvoice.invoiceTotal == null || editInvoice.invoiceTotal === ''
      ? null
      : Number(editInvoice.invoiceTotal)

  return {
    purchaseOrderNumber: editInvoice.purchaseOrderNumber || '',
    comment: editInvoice.notes || editInvoice.comment || '',
    documentType: editInvoice.documentType || DEFAULT_DOCUMENT_TYPE,
    supplier: editInvoice.supplierId || editInvoice.supplier || null,
    supplierTitle: editInvoice.supplierTitle || '',
    invoiceNumber: editInvoice.invoiceNumber || '',
    invoiceDate: normaliseInvoiceDateInput(editInvoice.invoiceDate) || '',
    invoiceTotalInput:
      totalPennies == null || !Number.isFinite(totalPennies)
        ? ''
        : formatPenniesAsPoundsInput(totalPennies),
  }
}

/**
 * Credit notes store a negative total; invoices store a positive total.
 * Preserves magnitude when flipping document type in the Edit modal.
 */
export function applyInvoiceTotalSignForDocumentType(documentType, pennies) {
  if (pennies == null || pennies === '') return pennies
  const value = Number(pennies)
  if (!Number.isFinite(value)) return pennies
  const abs = Math.abs(Math.round(value))
  return documentType === 'creditNote' ? -abs : abs
}

/**
 * When the chef flips Invoice ↔ Credit Note, keep the amount and flip the sign.
 */
export function applyDocumentTypeFlipToEditState(
  formState = {},
  nextDocumentType,
) {
  const nextType = nextDocumentType || DEFAULT_DOCUMENT_TYPE
  const next = {
    ...formState,
    documentType: nextType,
  }
  const pennies = parseInvoiceTotalToPennies(formState.invoiceTotalInput)
  if (pennies == null) return next

  const signed = applyInvoiceTotalSignForDocumentType(nextType, pennies)
  return {
    ...next,
    invoiceTotalInput: formatPenniesAsPoundsInput(signed),
  }
}

/** True when Edit form has a printed name but no OMS VenueSupplier id. */
export function isEditInvoiceNonOmsSupplier(formState = {}) {
  return isNonOmsSupplierUpload({
    supplier: formState.supplier,
    supplierTitle: formState.supplierTitle,
  })
}

/**
 * OMS supplier Select change: pick a venue supplier, or clear the link and
 * keep the printed name for Non-OMS editing.
 */
export function applyEditInvoiceSupplierSelect(
  formState = {},
  value,
  venueSuppliers = [],
) {
  if (!value) {
    return {
      ...formState,
      supplier: null,
    }
  }
  const selected = (venueSuppliers || []).find(
    row => String(row._id) === String(value),
  )
  return {
    ...formState,
    supplier: value,
    supplierTitle: selected?.supplierTitle || formState.supplierTitle || '',
  }
}

/** Free-text supplier name → Non-OMS (clears VenueSupplier id). */
export function applyEditInvoiceSupplierTitleInput(formState = {}, title) {
  return {
    ...formState,
    supplierTitle: title,
    supplier: null,
  }
}

/**
 * Map Edit-modal form state to the existing invoiceUpdate PATCH body.
 * Ensures credit-note totals are signed and cleared suppliers are explicit.
 */
export function buildEditInvoiceSavePayload(formState = {}) {
  const documentType = formState.documentType || DEFAULT_DOCUMENT_TYPE
  let totalPennies = parseInvoiceTotalToPennies(formState.invoiceTotalInput)
  if (totalPennies != null) {
    totalPennies = applyInvoiceTotalSignForDocumentType(
      documentType,
      totalPennies,
    )
  }
  const supplier = formState.supplier ? String(formState.supplier).trim() : null
  const supplierTitle = String(formState.supplierTitle || '').trim()
  const invoiceDate = String(formState.invoiceDate || '').trim()
  const hasSupplier = Boolean(supplier || supplierTitle)

  return {
    purchaseOrderNumber: String(formState.purchaseOrderNumber || '').trim(),
    comment: String(formState.comment || ''),
    documentType,
    detectedDocumentType:
      documentType === 'creditNote' ? 'credit_note' : 'invoice',
    supplier,
    supplierTitle,
    supplierConfirmedByUser: hasSupplier,
    invoiceNumber: String(formState.invoiceNumber || '').trim() || null,
    invoiceDate: invoiceDate || null,
    invoiceTotal: totalPennies,
  }
}

export function getDocumentTypeLabel(documentType) {
  return documentType === 'creditNote' ? 'Credit Note' : 'Invoice'
}

export function getDocumentTypeTagColor(documentType) {
  return documentType === 'creditNote' ? 'purple' : 'blue'
}
